import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Formik, Form, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import notify from 'components/atoms/Notify'
import Selection from 'components/molecules/Selection'
import apiPut from 'lib/network/apiPut'
import apiGet from 'lib/network/apiGet'

const BranchDiscounts = ({ close, onSuccess }) => {
  const { t } = useTranslation()
  const [companyNames, setCompanyNames] = useState([])
  const [branches, setBranches] = useState([])
  const [loadingCompanies, setLoadingCompanies] = useState(false)
  const [loadingBranches, setLoadingBranches] = useState(false)

  const resetDiscountValidationSchema = Yup.object({
    companyId: Yup.string().required(t('branchDiscount.validation.companyRequired')),
    branchId: Yup.string().required(t('branchDiscount.validation.branchRequired')),
    rentType: Yup.array().min(1, t('branchDiscount.validation.rentTypeRequired')).required(t('branchDiscount.validation.rentTypeRequired')),
  })

  const initialValues = {
    companyId: '',
    branchId: '',
    rentType: [],
  }

  const rentalTypes = [
    { id: 'dailyRate', name: t('branchDiscount.rentalTypes.dailyRate') },
    { id: 'weeklyRate', name: t('branchDiscount.rentalTypes.weeklyRate') },
    { id: 'monthlyRate', name: t('branchDiscount.rentalTypes.monthlyRate') },
    { id: 'yearlyRate', name: t('branchDiscount.rentalTypes.yearlyRate') },
  ]

  useEffect(() => {
    fetchCompanies()
  }, [])

  const fetchCompanies = async () => {
    setLoadingCompanies(true)
    const response = await apiGet('companies', { pageSize: 1000 })
    if (response?.data) {
      const companies = response.data.company.map(company => ({
        id: company._id,
        name: company.name,
      }))
      setCompanyNames(companies)
    } else {
      setCompanyNames([])
    }
    setLoadingCompanies(false)
  }

  const fetchBranches = async companyId => {
    try {
      setLoadingBranches(true)
      const response = await apiGet(`company/${companyId}/branches`)
      if (response?.data) {
        const branchList = response.data.map(branch => ({
          id: branch._id,
          name: branch.name,
        }))
        setBranches(branchList)
      } else {
        setBranches([])
      }
      setLoadingBranches(false)
    } catch (error) {
      notify.error(t('branchDiscount.messages.fetchBranchesFailed'))
      setBranches([])
      setLoadingBranches(false)
    }
  }

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setSubmitting(true)

      const response = await apiPut(`reset/branch/discount`, {
        companyId: values.companyId,
        branchId: values.branchId,
        rentType: values.rentType,
      })

      if (response.success) {
        notify.success(t('branchDiscount.messages.discountResetSuccess'))
        onSuccess?.()
        close()
      } else {
        notify.error(t('branchDiscount.messages.discountResetFailed'))
      }
    } catch (err) {
      notify.error(t('branchDiscount.messages.discountResetFailed'), err)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Formik initialValues={initialValues} validationSchema={resetDiscountValidationSchema} onSubmit={handleSubmit}>
      {({ values, setFieldValue, isSubmitting }) => (
        <Form className="space-y-6 sm:space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-normal text-gray-700 mb-1">{t('branchDiscount.form.company')}</label>
              <Selection
                title={loadingCompanies ? t('common.loading') : t('branchDiscount.form.selectCompanies')}
                list={companyNames}
                className="w-full"
                disabled={loadingCompanies}
                onChange={value => {
                  setFieldValue('companyId', value?.id || '')
                  setFieldValue('branchId', '')
                  setBranches([])
                  if (value?.id) {
                    fetchBranches(value.id)
                  }
                }}
                value={values.companyId}
              />
              <ErrorMessage name="companyId" component="p" className="text-red-600 text-sm mt-1" />
            </div>

            <div>
              <label className="block text-sm font-normal text-gray-700 mb-1">{t('branchDiscount.form.branch')}</label>
              <Selection
                title={t('branchDiscount.form.selectBranch')}
                list={branches}
                onChange={value => setFieldValue('branchId', value?.id || '')}
                value={values.branchId}
                className="w-full"
                disabled={!values.companyId || loadingBranches}
              />
              <ErrorMessage name="branchId" component="p" className="text-red-600 text-sm mt-1" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-normal text-gray-700 mb-1">{t('branchDiscount.form.rentalPeriod')}</label>
            <Selection
              onChange={ids => setFieldValue('rentType', ids || [])}
              title={t('branchDiscount.form.selectRentalPeriod')}
              list={rentalTypes}
              value={values.rentType}
              multiple
              className="w-full"
            />
            <ErrorMessage name="rentType" component="p" className="text-red-600 text-sm mt-1" />
          </div>

          <div className="mt-4 flex justify-end">
            <button
              type="submit"
              className={`inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm rounded-md text-white bg-dark-600 hover:bg-dark-700 ${
                isSubmitting ? 'opacity-70 cursor-not-allowed' : ''
              }`}
              disabled={isSubmitting}
            >
              {isSubmitting ? t('branchDiscount.buttons.resetting') : t('branchDiscount.buttons.resetDiscount')}
            </button>

            <button
              type="button"
              className="ml-2 rtl:ml-0 rtl:mr-2 inline-flex justify-center rounded-md bg-gray-100 px-4 py-2 text-sm font-medium text-gray-900 hover:bg-gray-200"
              onClick={close}
              disabled={isSubmitting}
            >
              {t('common.close')}
            </button>
          </div>
        </Form>
      )}
    </Formik>
  )
}

export default BranchDiscounts
